import { useEffect, useState } from 'react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';
import { AgentProgressRow } from './AgentProgressRow';
import { elapsedSeconds } from '@/lib/format';
import type { AgentRunDTO } from '@/types/db';

/**
 * `<AgentProgressList>` — UI-SPEC § 3.6.2 detail-view agent rows container.
 *
 * Wraps a glass-base panel around 7 `<AgentProgressRow>` children. Rows are
 * rendered in the locked execution order (matching the dot order in
 * UI-SPEC § 3.2). When the server omits an agent (e.g. before agent_runs
 * has been INSERTed), this component fills with a placeholder pending row
 * so the list height is stable and there's no layout shift on first poll.
 *
 * Live elapsed counter:
 * - Ticks every 1s independently of the polling cadence (UI-SPEC § 3.7
 *   requires the meta to update every second, even though the API only
 *   refreshes every 3s). The interval is only created when at least one
 *   agent is `running`, and is torn down on transition to terminal states.
 */
export interface AgentProgressListProps {
  agentRuns: AgentRunDTO[];
  /** Hyphenated server agent_name execution order. Override for tests. */
  executionOrder?: readonly string[];
  className?: string;
}

/**
 * Locked execution order matches UI-SPEC § 3.2 dot order. The server's
 * `/api/jobs/:id/status` returns hyphenated names (parity with v1.0 EJS),
 * so we order by those here. The mapping back to UI labels happens in
 * `<AgentProgressRow>` via `formatAgentLabel()`.
 */
const DEFAULT_EXEC_ORDER = [
  'walkability',
  'dog-friendliness',
  'flood-zone',
  'internet-providers',
  'entertainment',
  'property-images',
  'report-compiler',
] as const;

export function AgentProgressList({
  agentRuns,
  executionOrder = DEFAULT_EXEC_ORDER,
  className,
}: AgentProgressListProps) {
  // Live elapsed counter for running agents — ticks every 1s.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const hasRunning = agentRuns.some((a) => a.status === 'running');
    if (!hasRunning) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [agentRuns]);

  // Reorder + fill gaps. Missing rows get a placeholder pending row so
  // the height of the list is stable and there's no layout shift on first
  // poll.
  const byName = new Map(agentRuns.map((a) => [a.name, a]));
  const ordered: AgentRunDTO[] = executionOrder.map((serverName) => {
    const existing = byName.get(serverName);
    if (existing) return existing;
    return {
      name: serverName,
      status: 'pending',
      status_message: null,
      started_at: null,
      completed_at: null,
      error_message: null,
      current_step: 0,
      total_steps: 0,
      percentage: 0,
    };
  });

  return (
    <GlassPanel
      variant="base"
      as="section"
      aria-label="Agent progress"
      className={cn('p-4 md:p-6', className)}
    >
      <div role="list" className="flex flex-col">
        {ordered.map((agentRun) => {
          const liveElapsed =
            agentRun.status === 'running'
              ? elapsedSeconds(agentRun.started_at, null, now)
              : null;
          return (
            <AgentProgressRow
              key={agentRun.name}
              agentRun={agentRun}
              liveElapsedSec={liveElapsed}
            />
          );
        })}
      </div>
    </GlassPanel>
  );
}
